import { Injectable } from '@angular/core';

export interface DropdownOption {
  label: string;
  value: string;
}

export interface FilterCriteria {
  monthYear?: string;
  lob?: string;
  vendor?: string;
  jobTitle?: string;
  projectName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FilterHelperService {

  generateSimpleOptions(data: any[], key: string): DropdownOption[] {
    const uniqueValues = this.getUniqueValues(data, key);
    return uniqueValues.map(value => ({ label: value, value }));
  }

  getUniqueValues(data: any[], key: string): string[] {
    return [
      ...new Set(
        data
          .map(item => item[key])
          .filter(value => value?.trim())
      )
    ];
  }

  generateMonthYearOptions(data: any[], dateField: string = 'month'): DropdownOption[] {
    const monthYears = new Set<string>();
    
    data.forEach(item => {
      if (!item[dateField]) return;
      
      const date = new Date(item[dateField]);
      if (isNaN(date.getTime())) return;
      
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }).replace(' ', ', ');
      monthYears.add(`${label}|${monthYear}`);
    });

    return Array.from(monthYears)
      .sort()
      .map(item => {
        const [label, value] = item.split('|');
        return { label, value };
      });
  }

  generateVendorOptions(data: any[]): DropdownOption[] {
    const vendors = new Set<string>();
    
    data.forEach(item => {
      this.extractVendorNames(item.vendor, vendors);
      
      if (item.hiringVendorDtoList?.length) {
        item.hiringVendorDtoList.forEach((vendorObj: any) => {
          if (vendorObj.vendorName?.trim()) {
            vendors.add(vendorObj.vendorName.trim());
          }
        });
      }
    });

    return Array.from(vendors)
      .sort()
      .map(vendor => ({ label: vendor, value: vendor }));
  }

  extractFilterValues(filters: any): FilterCriteria {
    return {
      monthYear: filters.monthYear?.value || filters.monthYear,
      lob: filters.lob?.value || filters.lob,
      vendor: filters.vendor?.value || filters.vendor,
      jobTitle: filters.jobTitle?.value || filters.jobTitle,
      projectName: filters.projectName?.value || filters.projectName
    };
  }

  filterData(data: any[], filters: FilterCriteria): any[] {
    return data.filter(item => this.matchesAllFilters(item, filters));
  }

  matchesAllFilters(item: any, filters: FilterCriteria): boolean {
    return (
      this.matchesMonthYear(item.month, filters.monthYear) &&
      this.matchesSimpleField(item.lob, filters.lob) &&
      this.matchesVendor(item.vendor, filters.vendor) &&
      this.matchesSimpleField(item.jobTitle, filters.jobTitle) &&
      this.matchesSimpleField(item.project, filters.projectName)
    );
  }

  matchesSimpleField(itemValue: string, filterValue?: string): boolean {
    return !filterValue || itemValue === filterValue;
  }

  matchesMonthYear(itemDate: any, selectedDate?: string): boolean {
    if (!selectedDate || !itemDate) return true;
    
    try {
      const item = new Date(itemDate);
      const selected = new Date(selectedDate);
      
      if (isNaN(item.getTime()) || isNaN(selected.getTime())) return true;
      
      return (
        item.getFullYear() === selected.getFullYear() &&
        item.getMonth() === selected.getMonth()
      );
    } catch {
      return true;
    }
  }

  matchesVendor(vendorData: any, selectedVendor?: string): boolean {
    if (!selectedVendor || !vendorData) return true;

    if (Array.isArray(vendorData)) {
      return vendorData.some(vendor => 
        vendor?.toString().trim() === selectedVendor.trim()
      );
    }

    if (typeof vendorData === 'string') {
      return vendorData.trim() === selectedVendor.trim();
    }

    if (vendorData?.name) {
      return vendorData.name.trim() === selectedVendor.trim();
    }

    return false;
  }

  extractVendorNames(vendorData: any, vendors: Set<string>): void {
    if (!vendorData) return;

    if (Array.isArray(vendorData)) {
      vendorData.forEach(vendor => {
        const name = this.getVendorName(vendor);
        if (name) vendors.add(name);
      });
    } else {
      const name = this.getVendorName(vendorData);
      if (name) vendors.add(name);
    }
  }

  getVendorName(vendor: any): string | null {
    if (typeof vendor === 'string') {
      return vendor.trim() || null;
    }
    
    if (vendor?.name?.trim()) {
      return vendor.name.trim();
    }
    
    if (vendor?.vendorName?.trim()) {
      return vendor.vendorName.trim();
    }
    
    return null;
  }
}